import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Search, CreditCard, ShieldAlert, RefreshCw } from "lucide-react";

// Currencies come dynamically from data; these are just filter-shortcut suggestions.
const CURRENCY_HINTS = ["", "USD", "EUR", "MXN"];

export default function AdminStripe({ embedded = false }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [currency, setCurrency] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (email) params.set("email", email);
      if (currency) params.set("currency", currency);
      const [txR, totR] = await Promise.all([
        api.get(`/admin/stripe/transactions${params.toString() ? `?${params.toString()}` : ""}`),
        api.get("/admin/stripe/totals"),
      ]);
      setRows(txR.data || []);
      setTotals(totR.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron cargar las transacciones.");
    } finally {
      setLoading(false);
    }
  };

  const currenciesInData = useMemo(() => {
    const s = new Set(CURRENCY_HINTS);
    rows.forEach((r) => r.currency && s.add(r.currency));
    return Array.from(s);
  }, [rows]);

  useEffect(() => { load(); }, []);

  // Server-side guard is the source of truth; this is UX-only
  if (!user?.is_admin) return <Navigate to="/panel" replace />;

  const inner = (
    <div className="space-y-8 max-w-6xl" data-testid="admin-stripe-page">

      {/* Totals by currency */}
      <section className="mep-card p-7">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard size={18} className="text-[var(--mep-gold)]" />
          <h2 className="font-display text-xl font-light tracking-tight">Totales por moneda (solo pagos exitosos)</h2>
        </div>
        {totals.length === 0 ? (
          <p className="text-sm text-[var(--mep-text-muted)]">Aún no hay pagos completados.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stripe-totals">
            {totals.map((t) => (
              <div
                key={t.currency}
                data-testid={`stripe-total-${t.currency}`}
                className="p-4 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">{t.currency}</div>
                <div className="font-display text-3xl font-light text-[var(--mep-gold)] mt-1">
                  {t.total.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-[var(--mep-text-muted)] mt-1">{t.count} pago(s)</div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-[var(--mep-text-muted)] mt-4">
          Los totales nunca se suman entre monedas distintas.
        </p>
      </section>

      {/* Filters */}
      <section className="mep-card p-7">
        <div className="flex items-center gap-3 mb-4">
          <Search size={18} className="text-[var(--mep-pink)]" />
          <h2 className="font-display text-xl font-light tracking-tight">Filtrar transacciones</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            data-testid="stripe-filter-email"
            placeholder="Buscar por correo"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-2.5 text-sm"
          />
          <select
            data-testid="stripe-filter-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-3 py-2.5 text-sm"
          >
            {currenciesInData.map((c) => (
              <option key={c} value={c}>{c ? c : "Todas las monedas"}</option>
            ))}
          </select>
          <button
            data-testid="stripe-apply-filters"
            onClick={load}
            disabled={loading}
            className="btn-pink w-full py-2.5 font-semibold text-sm flex items-center justify-center gap-2"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Cargando..." : "Aplicar filtros"}
          </button>
        </div>
        <p className="text-xs text-[var(--mep-text-muted)] mt-3">
          Esta tabla muestra únicamente pagos Stripe exitosos (pagados). Las sesiones abiertas, pendientes o falladas no se listan pero permanecen registradas en la base de datos.
        </p>
      </section>

      {/* Transactions table */}
      <section className="mep-card p-7">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-light tracking-tight">Transacciones Stripe</h2>
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">
            {rows.length} {rows.length === 1 ? "resultado" : "resultados"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stripe-transactions-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.18em] text-[var(--mep-text-muted)] border-b border-[var(--mep-border)]">
                <th className="py-3 pr-4">Fecha</th>
                <th className="py-3 pr-4">Correo</th>
                <th className="py-3 pr-4">Nombre</th>
                <th className="py-3 pr-4">Importe</th>
                <th className="py-3 pr-4">Moneda</th>
                <th className="py-3 pr-4">Estado pago</th>
                <th className="py-3 pr-4">Premium</th>
                <th className="py-3 pr-4">Session ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-[var(--mep-text-muted)]">
                    Sin resultados con los filtros actuales.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--mep-border)]/60">
                  <td className="py-3 pr-4 whitespace-nowrap text-[var(--mep-text-muted)]">
                    {formatDate(r.created_at)}
                  </td>
                  <td className="py-3 pr-4">{r.email || "—"}</td>
                  <td className="py-3 pr-4">{r.name || "—"}</td>
                  <td className="py-3 pr-4 font-medium">
                    {r.amount != null ? Number(r.amount).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-xs uppercase tracking-[0.18em]">{r.currency || "—"}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <StatusPill value={r.payment_status} />
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`text-xs uppercase tracking-[0.18em] ${r.premium_status === "paid" ? "text-[var(--mep-gold)]" : "text-[var(--mep-text-muted)]"}`}>
                      {r.premium_status || "—"}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-xs text-[var(--mep-text-muted)] font-mono">
                    {r.session_id ? `${r.session_id.slice(0, 14)}…` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="text-xs text-[var(--mep-text-muted)] flex items-center gap-2 pt-4">
        <ShieldAlert size={14} /> Solo lectura — no se puede modificar, reembolsar ni cancelar desde este panel.
      </div>
    </div>
  );

  if (embedded) return inner;
  return inner;
}

const StatusPill = ({ value }) => {
  const v = (value || "").toLowerCase();
  let cls = "text-[var(--mep-text-muted)] border-[var(--mep-border)]";
  if (v === "paid") cls = "text-[var(--mep-gold)] border-[var(--mep-gold)]/40 bg-[rgba(212,175,55,0.08)]";
  else if (v === "failed") cls = "text-[var(--mep-pink)] border-[var(--mep-pink)]/40 bg-[rgba(232,61,132,0.08)]";
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.18em] border rounded-full px-2.5 py-1 ${cls}`}>
      {value || "—"}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};
